// @flow
import React, { useRef, useState } from 'react';
import { themr } from 'react-css-themr';
import { Box, Flex } from '@locus-taxy/neo-ui';
import Chip from 'common/components/core/common/Chip';
import Input, { InputTypes } from 'common/components/core/input';
import baseTheme from './MultiChipInput.scss';

type Props = {
    labels: Array<string>,
    theme: { [string]: string },
    maxLimit: number,
    dataTestId: string,
    showSaveOptions: boolean,
    onDeleteAll: () => void,
    onDelete: (s: string, index?: number) => void,
    onEnter?: (e: Event, label: string) => void,
    onFocus?: () => void,
    onBlur?: () => void,
    createChipOnBlur?: boolean // add a chip with the input value on blur
};

const MultiChipInput = ({
    labels,
    theme,
    maxLimit,
    onDelete,
    onEnter,
    backgroundColor = 'transparent',
    borderColor = 'grey.200',
    autoFocus = true,
    onFocus,
    onBlur,
    createChipOnBlur
}: Props) => {
    const inputRef = useRef(null);
    const [input, setInput] = useState('');

    const onChange = val => {
        if (val.trim() || (input && !val.trim())) {
            setInput(val);
        }
    };

    const onClearChip = (id, index) => () => {
        onDelete(id, index);
    };

    const onKeyPress = e => {
        e.stopPropagation();
        if (e.key === 'Enter' && input) {
            onEnter?.(e, input);
            setInput('');
        }
    };

    const handleOnBlur = e => {
        e.stopPropagation();
        if (createChipOnBlur && input) {
            onEnter?.(e, input);
            setInput('');
        }
        onBlur?.();
    };

    const isInputDisabled = labels.length >= maxLimit;

    const chips = labels.map((chipText, index) => (
        <Chip
            key={chipText}
            className={theme.tagChip}
            showOverflowTooltip
            text={chipText}
            theme={theme}
            rightIcon="cross-circle"
            rightIconProps={{
                onClick: onClearChip(chipText, index)
            }}
        />
    ));

    const inputProps = {
        theme,
        inputType: InputTypes.NoBorder,
        onChange,
        onKeyPress,
        onBlur: handleOnBlur,
        ref: inputRef,
        disabled: isInputDisabled,
        value: input,
        autoFocus,
        onFocus
    };

    return (
        <Box
            width="100%"
            border="1px solid"
            borderColor={borderColor}
            backgroundColor={backgroundColor}
            pt={3}
            pb={0}
            px={0}
            className={theme.multiChipInput}
        >
            <Flex flexWrap="wrap" maxHeight="50px" overflow="auto">
                {chips}
                <Input {...inputProps} className={baseTheme.adjustableWidth} />
            </Flex>
        </Box>
    );
};

export default themr('ThemedMultiChipInput', baseTheme)(MultiChipInput);
